<template>
    <div class="login-container">
      <div class="login-header">
        <img class="login-logo" alt="HR3" />
      </div>
  
      <main class="reset-content">
        <!-- Проверка ссылки -->
  
        <template v-if="pageState === 'checking'">
          <h2 class="title">
            Проверяем ссылку
          </h2>
  
          <p class="description">
            Подождите, пожалуйста. Мы проверяем ссылку
            восстановления пароля.
          </p>
  
          <div class="inline-spinner"></div>
        </template>
  
        <!-- Ошибка ссылки -->
  
        <template v-else-if="pageState === 'error'">
          <div class="status-icon error-icon">
            !
          </div>
  
          <h2 class="title">
            Ссылка недействительна
          </h2>
  
          <p class="description">
            {{ errorMessage }}
          </p>
  
          <button
            type="button"
            class="primary-button"
            @click="goToLogin"
          >
            Вернуться ко входу
          </button>
        </template>
  
        <!-- Пароль успешно изменён -->
  
        <template v-else-if="pageState === 'success'">
          <div class="status-icon success-icon">
            ✓
          </div>
  
          <h2 class="title">
            Пароль изменён
          </h2>
  
          <p class="description">
            Новый пароль успешно установлен.
            Теперь вы можете войти в аккаунт.
          </p>
  
          <button
            type="button"
            class="primary-button"
            @click="goToLogin"
          >
            Войти в аккаунт
          </button>
        </template>
  
        <!-- Форма нового пароля -->
  
        <template v-else>
          <h2 class="title">
            Новый пароль
          </h2>
  
          <p class="description">
            Установите новый пароль для аккаунта
          </p>
  
          <p class="account-email">
            {{ accountEmail }}
          </p>
  
          <form
            class="reset-form"
            @submit.prevent="submitNewPassword"
          >
            <div class="form-group">
              <label
                for="new-password"
                class="form-label"
              >
                Новый пароль
              </label>
  
              <div class="password-wrapper">
                <input
                  id="new-password"
                  v-model="newPassword"
                  :type="showNewPassword ? 'text' : 'password'"
                  class="form-input"
                  placeholder="Введите новый пароль"
                  autocomplete="new-password"
                  maxlength="128"
                  @input="clearFormError"
                />
  
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="
                    showNewPassword
                      ? 'Скрыть пароль'
                      : 'Показать пароль'
                  "
                  @click="showNewPassword = !showNewPassword"
                >
                  {{ showNewPassword ? "Скрыть" : "Показать" }}
                </button>
              </div>
            </div>
  
            <div class="form-group">
              <label
                for="repeat-password"
                class="form-label"
              >
                Повторите пароль
              </label>
  
              <div class="password-wrapper">
                <input
                  id="repeat-password"
                  v-model="repeatPassword"
                  :type="
                    showRepeatPassword
                      ? 'text'
                      : 'password'
                  "
                  class="form-input"
                  placeholder="Повторите новый пароль"
                  autocomplete="new-password"
                  maxlength="128"
                  @input="clearFormError"
                />
  
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="
                    showRepeatPassword
                      ? 'Скрыть пароль'
                      : 'Показать пароль'
                  "
                  @click="
                    showRepeatPassword = !showRepeatPassword
                  "
                >
                  {{
                    showRepeatPassword
                      ? "Скрыть"
                      : "Показать"
                  }}
                </button>
              </div>
            </div>
  
            <p class="password-hint">
              Пароль должен содержать минимум 6 символов.
            </p>
  
            <p
              v-if="formError"
              class="form-error"
            >
              {{ formError }}
            </p>
  
            <button
              type="submit"
              class="primary-button"
              :disabled="isSubmitting"
            >
              {{
                isSubmitting
                  ? "Сохраняем..."
                  : "Установить новый пароль"
              }}
            </button>
          </form>
        </template>
      </main>
  
      <Footer />
  
      <div
        v-if="isSubmitting"
        class="overlay"
      >
        <div class="spinner"></div>
      </div>
    </div>
  </template>
  
  <script setup>
  import {
    onMounted,
    ref
  } from "vue";
  
  import {
    useRoute,
    useRouter
  } from "vue-router";
  
  import {
    confirmPasswordReset,
    getAuth,
    verifyPasswordResetCode
  } from "firebase/auth";
  
  import Footer from "../components/Footer.vue";
  
  
  const route = useRoute();
  const router = useRouter();
  const auth = getAuth();
  
  
  const pageState = ref("checking");
  
  const accountEmail = ref("");
  const actionCode = ref("");
  
  const newPassword = ref("");
  const repeatPassword = ref("");
  
  const showNewPassword = ref(false);
  const showRepeatPassword = ref(false);
  
  const isSubmitting = ref(false);
  
  const errorMessage = ref("");
  const formError = ref("");
  
  
  const getQueryString = (value) => {
    if (Array.isArray(value)) {
      return value[0] || "";
    }
  
    return typeof value === "string"
      ? value
      : "";
  };
  
  
  const getFirebaseErrorMessage = (error) => {
    switch (error?.code) {
      case "auth/expired-action-code":
        return (
          "Срок действия ссылки истёк. " +
          "Запросите новое письмо восстановления."
        );
  
      case "auth/invalid-action-code":
        return (
          "Ссылка недействительна или уже была использована. " +
          "Запросите новое письмо восстановления."
        );
  
      case "auth/user-disabled":
        return "Этот аккаунт заблокирован.";
  
      case "auth/user-not-found":
        return "Аккаунт пользователя не найден.";
  
      case "auth/weak-password":
        return (
          "Пароль слишком простой. " +
          "Придумайте более надёжный пароль."
        );
  
      case "auth/network-request-failed":
        return (
          "Не удалось подключиться к серверу. " +
          "Проверьте интернет-соединение."
        );
  
      default:
        return (
          "Не удалось изменить пароль. " +
          "Попробуйте запросить новую ссылку."
        );
    }
  };
  
  
  const clearFormError = () => {
    formError.value = "";
  };
  
  
  const validatePasswords = () => {
    if (!newPassword.value) {
      formError.value = "Введите новый пароль.";
      return false;
    }
  
    if (newPassword.value.length < 6) {
      formError.value = (
        "Пароль должен содержать минимум 6 символов."
      );
  
      return false;
    }
  
    if (newPassword.value.length > 128) {
      formError.value = (
        "Пароль не должен превышать 128 символов."
      );
  
      return false;
    }
  
    if (!repeatPassword.value) {
      formError.value = "Повторите новый пароль.";
      return false;
    }
  
    if (
      newPassword.value !==
      repeatPassword.value
    ) {
      formError.value = "Пароли не совпадают.";
      return false;
    }
  
    return true;
  };
  
  
  const submitNewPassword = async () => {
    formError.value = "";
  
    if (!validatePasswords()) {
      return;
    }
  
    if (!actionCode.value) {
      pageState.value = "error";
      errorMessage.value = (
        "Код восстановления пароля отсутствует."
      );
  
      return;
    }
  
    isSubmitting.value = true;
  
    try {
      await confirmPasswordReset(
        auth,
        actionCode.value,
        newPassword.value
      );
  
      console.log(
        "✅ Новый пароль успешно установлен"
      );
  
      newPassword.value = "";
      repeatPassword.value = "";
  
      pageState.value = "success";
    } catch (error) {
      console.error(
        "❌ Ошибка установки нового пароля:",
        error
      );
  
      formError.value = (
        getFirebaseErrorMessage(error)
      );
    } finally {
      isSubmitting.value = false;
    }
  };
  
  
  const goToLogin = () => {
    router.replace("/login");
  };
  
  
  onMounted(async () => {
    console.log(
      "📩 Начинаем восстановление пароля..."
    );
  
    const mode = getQueryString(
      route.query.mode
    );
  
    actionCode.value = getQueryString(
      route.query.oobCode
    );
  
    console.log(
      "🧐 Полученный mode:",
      mode || "отсутствует"
    );
  
    console.log(
      "🔑 Полученный oobCode:",
      actionCode.value
        ? "присутствует"
        : "отсутствует"
    );
  
    if (
      mode &&
      mode !== "resetPassword"
    ) {
      pageState.value = "error";
  
      errorMessage.value = (
        "Эта ссылка не предназначена " +
        "для восстановления пароля."
      );
  
      return;
    }
  
    if (!actionCode.value) {
      pageState.value = "error";
  
      errorMessage.value = (
        "Код восстановления отсутствует. " +
        "Запросите новое письмо."
      );
  
      return;
    }
  
    try {
      accountEmail.value = (
        await verifyPasswordResetCode(
          auth,
          actionCode.value
        )
      );
  
      console.log(
        "✅ Ссылка восстановления действительна"
      );
  
      pageState.value = "ready";
    } catch (error) {
      console.error(
        "❌ Ошибка проверки ссылки:",
        error
      );
  
      errorMessage.value = (
        getFirebaseErrorMessage(error)
      );
  
      pageState.value = "error";
    }
  });
  </script>
  
  <style scoped>
  .login-container {
    width: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    background-color: var(--bg);
    overflow-x: hidden;
    position: relative;
  }
  
  .login-header {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 750px;
    background-size: cover;
    background-position: center;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 20px;
  }
  
  [data-theme="dark"] .login-header {
    background-image:
      url("../assets/login_header_dark.webp");
  }
  
  [data-theme="light"] .login-header {
    background-image:
      url("../assets/login_header_light.webp");
  }
  
  .login-logo {
    width: 180px;
    height: auto;
    max-width: 100%;
    transform: translateY(-20px);
    content: url("../assets/login_logo_dark.png");
  }
  
  [data-theme="light"] .login-logo {
    content: url("../assets/login_logo_light.png");
  }
  
  .reset-content {
    width: calc(100% - 40px);
    max-width: 500px;
    margin: 170px auto 60px;
    padding: 32px;
    box-sizing: border-box;
    position: relative;
    z-index: 2;
    text-align: center;
    font-family: "Eastman-Regular", sans-serif;
    letter-spacing: -0.4px;
    border-radius: 24px;
    background: var(--bg);
  }
  
  .title {
    margin: 0 0 16px;
    font-family: "Eastman-Bold", sans-serif;
    font-size: 26px;
    line-height: 34px;
    letter-spacing: -0.9px;
  }
  
  .description {
    margin: 0 auto 12px;
    font-size: 16px;
    line-height: 24px;
  }
  
  .account-email {
    margin: 0 0 28px;
    font-family: "Eastman-Bold", sans-serif;
    font-size: 16px;
    line-height: 24px;
    overflow-wrap: anywhere;
  }
  
  .reset-form {
    width: 100%;
    text-align: left;
  }
  
  .form-group {
    width: 100%;
    margin-bottom: 18px;
  }
  
  .form-label {
    display: block;
    margin-bottom: 8px;
    font-family: "Eastman-Bold", sans-serif;
    font-size: 14px;
    line-height: 20px;
  }
  
  .password-wrapper {
    width: 100%;
    position: relative;
  }
  
  .form-input {
    width: 100%;
    height: 52px;
    padding: 0 98px 0 16px;
    box-sizing: border-box;
    border: 1px solid #D1D5DB;
    border-radius: 14px;
    outline: none;
    background: transparent;
    color: inherit;
    font-family: "Eastman-Regular", sans-serif;
    font-size: 16px;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }
  
  .form-input:focus {
    border-color: #2887F7;
    box-shadow:
      0 0 0 3px rgba(40, 135, 247, 0.15);
  }
  
  .form-input::placeholder {
    color: #9CA3AF;
  }
  
  .password-toggle {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    padding: 4px;
    border: 0;
    background: transparent;
    color: #2887F7;
    font-family: "Eastman-Bold", sans-serif;
    font-size: 12px;
    cursor: pointer;
  }
  
  .password-hint {
    margin: -4px 0 18px;
    color: #6B7280;
    font-size: 13px;
    line-height: 19px;
  }
  
  .form-error {
    margin: 0 0 16px;
    color: #EF4444;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }
  
  .primary-button {
    width: 100%;
    min-height: 52px;
    padding: 14px 20px;
    border: 0;
    border-radius: 14px;
    background-color: #2887F7;
    color: #FFFFFF;
    font-family: "Eastman-Bold", sans-serif;
    font-size: 16px;
    line-height: 22px;
    cursor: pointer;
    transition:
      opacity 0.2s ease,
      transform 0.2s ease;
  }
  
  .primary-button:hover {
    opacity: 0.92;
  }
  
  .primary-button:active {
    transform: scale(0.99);
  }
  
  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .status-icon {
    width: 76px;
    height: 76px;
    margin: 0 auto 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #FFFFFF;
    font-family: "Eastman-Bold", sans-serif;
    font-size: 40px;
  }
  
  .success-icon {
    background-color: #22C55E;
  }
  
  .error-icon {
    background-color: #EF4444;
  }
  
  .inline-spinner {
    width: 42px;
    height: 42px;
    margin: 28px auto 0;
    border: 4px solid rgba(40, 135, 247, 0.2);
    border-top-color: #2887F7;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  .overlay {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.55);
  }
  
  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top-color: #FFFFFF;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  footer {
    width: 100%;
    position: relative;
    z-index: 5;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  @media (max-width: 768px) {
    .login-header {
      height: 420px;
    }
  
    .login-logo {
      width: 140px;
    }
  
    .reset-content {
      width: calc(100% - 32px);
      margin-top: 130px;
      margin-bottom: 40px;
      padding: 24px 18px;
      border-radius: 20px;
    }
  
    .title {
      font-size: 22px;
      line-height: 29px;
    }
  
    .description {
      font-size: 14px;
      line-height: 21px;
    }
  
    .account-email {
      margin-bottom: 22px;
      font-size: 14px;
      line-height: 21px;
    }
  
    .form-input {
      height: 50px;
      padding-right: 92px;
      font-size: 16px;
    }
  
    .primary-button {
      min-height: 50px;
      font-size: 15px;
    }
  
    .status-icon {
      width: 66px;
      height: 66px;
      font-size: 34px;
    }
  }
  </style>